import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  FormControl,
  RadioGroup,
  Radio,
  Text,
  Icon,
} from '@chakra-ui/react';
import * as mdIcons from 'react-icons/md';
import { CustomAlert } from 'components/alerts/CustomAlert';
import FilePicker from 'components/filePicker/file-picker';
import { ApiMassLoadCoverageParameterFile } from 'api/coverage/inputParameters';
import { Formik, FormikProps } from 'formik';
import { useState } from 'react';
import { useLoading } from 'contexts/LoadingContext';

const PARAMETER_NAME = 'products_forecast';

export type ForecastUploadModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onUploaded: () => void;
};

type FormValues = {
  mode: string;
  file: File | null;
};

export function ForecastUploadModal(props: ForecastUploadModalProps): JSX.Element {
  const { isOpen, onClose, onUploaded } = props;
  const { show, hide } = useLoading();
  const [error, setError] = useState<{ status?: number; data?: { message?: string } } | null>(
    null,
  );

  const templateHref =
    (process.env.REACT_APP_TEMPLATE_URL ?? '') + `${PARAMETER_NAME}/template.xlsx`;

  const submit = (values: FormValues) => {
    if (!values.file) {
      setError({ status: 400, data: { message: 'Seleccione un archivo para cargar.' } });
      return;
    }
    show();
    ApiMassLoadCoverageParameterFile(
      PARAMETER_NAME,
      values.file,
      values.mode ? values.mode : 'update',
    )
      .then(() => {
        hide();
        setError(null);
        onUploaded();
        onClose();
      })
      .catch((err) => {
        hide();
        setError(err.response);
      });
  };

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={() => {
        setError(null);
        onClose();
      }}
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent maxW="lg">
        <ModalHeader>Carga masiva de Forecast</ModalHeader>
        <ModalCloseButton />
        <Formik<FormValues>
          enableReinitialize
          initialValues={{ mode: 'update', file: null }}
          onSubmit={(values) => submit(values)}
        >
          {(formik: FormikProps<FormValues>) => (
            <form onSubmit={formik.handleSubmit}>
              <ModalBody>
                {error != null ? (
                  <CustomAlert
                    status="error"
                    message={
                      error.status === 400 && error.data?.message
                        ? error.data.message
                        : 'Ocurrió un error procesando el archivo.'
                    }
                    mb="24px"
                  />
                ) : null}
                <CustomAlert
                  status="warning"
                  message='El modo "Sustituir" reemplaza por completo el forecast cargado. Use "Actualizar" para combinar con registros existentes.'
                  mb="24px"
                />
                <FormControl>
                  <Box display="flex" justifyContent="space-between" pb="8px" flexWrap="wrap" gap={3}>
                    <RadioGroup
                      value={formik.values.mode}
                      onChange={(v: string) => formik.setFieldValue('mode', v)}
                    >
                      <Text as="span" fontWeight="600" me={3}>
                        Modo
                      </Text>
                      <Radio value="update" me={4}>
                        Actualizar
                      </Radio>
                      <Radio value="clear">Sustituir</Radio>
                    </RadioGroup>
                    <Button
                      as="a"
                      href={templateHref}
                      download
                      colorScheme="blue"
                      size="sm"
                      variant="outline"
                    >
                      <Icon as={mdIcons.MdDownloadDone} h="16px" w="16px" me="8px" />
                      Plantilla
                    </Button>
                  </Box>
                  <FilePicker
                    inputProps={{
                      'aria-required': true,
                      size: 'lg',
                      mb: '8px',
                      fontWeight: '500',
                      variant: 'auth',
                      fontSize: 'sm',
                      mt: '8px',
                    }}
                    onFileChange={(files) =>
                      formik.setFieldValue('file', files.length ? files[0] : null)
                    }
                    placeholder="Archivo de forecast (.xlsx / .csv)"
                    clearButtonLabel="Limpiar"
                    multipleFiles={false}
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    hideClearButton
                  />
                </FormControl>
              </ModalBody>
              <ModalFooter justifyContent="flex-end" gap={3}>
                <Button variant="ghost" onClick={onClose}>
                  Cancelar
                </Button>
                <Button type="submit" colorScheme="blue">
                  Enviar
                </Button>
              </ModalFooter>
            </form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
}
